import React, {useRef, useState} from 'react';
import styled from 'styled-components';
import { Colors, Fonts } from '../utils/colors';
import { formatFileSize } from '../utils/util';
import { MAX_FILE_BYTES, MAX_PAD_FILE_BYTES } from '../utils/fetch';

export const FileAttach = (props: {
  files: File[]
  onChange: (files: File[]) => void
  disabled?: boolean
  /** 0–100 while uploading; omit or null when idle */
  uploadProgress?: number | null
  uploadLabel?: string | null
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const openPicker = () => {
    if (props.disabled) {
      return
    }
    if (inputRef.current) {
      inputRef.current.click()
    }
  }

  const mergeFiles = (incoming: FileList | File[]) => {
    const list = Array.from(incoming as ArrayLike<File>)
    if (list.length === 0) {
      return
    }

    const next = props.files.slice()
    let err: string | null = null
    for (let i = 0; i < list.length; i++) {
      const file = list[i]
      if (file.size <= 0) {
        err = `"${file.name}" is empty`
        continue
      }
      if (file.size > MAX_FILE_BYTES) {
        err = `"${file.name}" is over 25 MB`
        continue
      }
      const already = next.some(
        (f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified
      )
      if (already) {
        continue
      }
      next.push(file)
    }

    const total = next.reduce((sum, f) => sum + f.size, 0)
    if (total > MAX_PAD_FILE_BYTES) {
      setError("Selected files exceed the 100 MB pad limit")
      return
    }

    setError(err)
    props.onChange(next)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      mergeFiles(event.target.files)
    }
    event.target.value = ""
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
    if (props.disabled) {
      return
    }
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      mergeFiles(event.dataTransfer.files)
    }
  }

  const removeAt = (index: number) => {
    if (props.disabled) {
      return
    }
    const next = props.files.filter((_, i) => i !== index)
    props.onChange(next)
    setError(null)
  }

  const progress = props.uploadProgress
  const showProgress = progress != null && progress >= 0

  return (
    <Wrap>
      <input
        ref={inputRef}
        type="file"
        hidden
        multiple
        onChange={handleChange}
      />
      <DropZone
        type="button"
        disabled={props.disabled}
        $dragging={isDragging}
        $disabled={!!props.disabled}
        onClick={openPicker}
        onDragEnter={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (!props.disabled) {
            setIsDragging(true)
          }
        }}
        onDragOver={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setIsDragging(false)
        }}
        onDrop={handleDrop}
      >
        <DropTitle>attach files</DropTitle>
        <DropHint>
          drop here or click · up to 25 MB each · 100 MB per pad
        </DropHint>
      </DropZone>

      {props.files.length > 0 && (
        <FileList>
          {props.files.map((file, i) => (
            <FileRow key={`${file.name}-${file.size}-${file.lastModified}-${i}`}>
              <Name title={file.name}>{file.name}</Name>
              <Meta>{formatFileSize(file.size)}</Meta>
              <Clear
                type="button"
                disabled={props.disabled}
                onClick={() => removeAt(i)}
                aria-label={`Remove ${file.name}`}
              >
                x
              </Clear>
            </FileRow>
          ))}
        </FileList>
      )}

      {showProgress && (
        <ProgressBlock>
          <ProgressLabel>
            {props.uploadLabel || "uploading"} · {Math.round(progress as number)}%
          </ProgressLabel>
          <ProgressTrack>
            <ProgressFill style={{ width: `${Math.min(100, progress as number)}%` }} />
          </ProgressTrack>
        </ProgressBlock>
      )}

      {error && <ErrorLine>{error}</ErrorLine>}
    </Wrap>
  )
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: ${Fonts.mono};
`;

const DropZone = styled.button<{ $dragging: boolean; $disabled: boolean }>`
  appearance: none;
  width: 100%;
  margin: 0;
  padding: 16px 14px;
  border: 1px dashed ${props =>
    props.$dragging ? Colors.destructGreen : Colors.terminalBorder};
  background: ${props =>
    props.$dragging ? Colors.accentFaint : Colors.fieldBg};
  color: ${Colors.terminalMuted};
  text-align: left;
  cursor: ${props => props.$disabled ? "default" : "pointer"};
  opacity: ${props => props.$disabled ? 0.55 : 1};
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;

  &:hover:not(:disabled),
  &:focus-visible {
    border-color: ${Colors.destructGreen};
    box-shadow: 0 0 0 1px ${Colors.accentRing};
    outline: none;
  }
`;

const DropTitle = styled.div`
  color: ${Colors.destructGreen};
  font-size: 13px;
  letter-spacing: 0.04em;
  text-shadow: 0 0 8px ${Colors.terminalGlow};
`;

const DropHint = styled.div`
  margin-top: 6px;
  font-size: 11px;
  line-height: 1.45;
  color: ${Colors.terminalDim};
`;

const FileList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FileRow = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 12px;
  color: ${Colors.terminalMuted};
`;

const Name = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${Colors.terminalText};
`;

const Meta = styled.span`
  flex-shrink: 0;
`;

const Clear = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  color: ${Colors.terminalDim};
  font-family: ${Fonts.mono};
  font-size: 12px;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${Colors.destructRed};
  }

  &:disabled {
    cursor: default;
  }
`;

const ProgressBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ProgressLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.04em;
  color: ${Colors.terminalMuted};
`;

const ProgressTrack = styled.div`
  height: 4px;
  background: ${Colors.accentWash};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${Colors.destructGreen};
  box-shadow: 0 0 10px ${Colors.terminalGlow};
  transition: width 0.12s ease;
`;

const ErrorLine = styled.div`
  font-size: 12px;
  color: ${Colors.destructRed};
`;
